import { ensureActiveSession } from "@lib/auth/ensureActiveSession";
import { COMMERCETOOLS_API_URL } from "@lib/constants/urlConstants";
import { getSession, useSession } from "next-auth/react";
import type { RequestMiddleware } from "graphql-request";
import { GraphQLClient } from "graphql-request";
import type { Requester } from "./generated/sdk";
import { getSdk } from "./generated/sdk";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";

type UseClientParams = {
    /**
     * If true, the GraphQL client will always fetch a new token from the server.
     */
    forceSessionFetch?: boolean;
};

// Custom fetch to handle errors without throwing.
const fetchWithErrorResponse = async (input: RequestInfo | URL, init?: RequestInit) => {
    const originalResponse = await fetch(input, init);
    const response = originalResponse.clone();
    try {
        const json = await response.json();
        // This nested structure of the GraphQL JSON is required to make sure we always get all the data when using GraphQLClient.request.
        // Internal function requires errors to be in the root of payload but only returns what's inside the `data` field.
        const payload = {
            ...json,
            data: {
                ...json,
                status: response.status,
            },
        };

        return new Response(JSON.stringify(payload), {
            headers: {
                "Content-Type": "application/json",
            },
        });
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to parse JSON response in GraphQL client:", error);
    }

    return originalResponse;
};

/**
 * Get GraphQL Client in browser environments (frontend).
 *
 * If the user has an active session, it will add an accessToken to all requests
 */
export const useCommerceClient = (params?: UseClientParams | undefined) => {
    // TODO: Deprecate forceSessionFetch if it's not needed anywhere.
    const { forceSessionFetch = false } = params || {};
    // const api = useStoreApi();
    const { data: session, status } = useSession();

    const getAuthSession = async () => {
        if (status !== "authenticated") {
            return null;
        }
        if (forceSessionFetch) {
            return await getSession();
        }
        // We currently have an issue with the GraphQL client not receiving updated tokens in time when calls are made within a single function.
        // This is a workaround to ensure we get an updated token.
        return await ensureActiveSession(session);
    };

    const requestAuthMiddleware: RequestMiddleware = async (operation) => {
        const authSession = await getAuthSession();
        if (!authSession) {
            Logger.error(
                ServiceType.COMMERCE_TOOLS,
                "CommerceTools GraphQL client called without an active session",
                {
                    tag: LogTag.AUTHENTICATION,
                    operation,
                }
            );
        }
        return {
            ...operation,
            headers: {
                ...operation.headers,
                Authorization: `Bearer ${authSession?.accessToken}`,
            },
        };
    };

    const client = new GraphQLClient(
        `${COMMERCETOOLS_API_URL}/${process.env.NEXT_PUBLIC_COMMERCETOOLS_PROJECT_KEY}/graphql`,
        {
            requestMiddleware: requestAuthMiddleware,
            // This allows us to get the errors and data returned as JSON.
            // Without this the library will throw an error and lose the GraphQL error data in the response.
            fetch: fetchWithErrorResponse,
            errorPolicy: "all",
        }
    );

    // grahpql-codegen also has an option to generate from graphql-request, but it doesn't support requestMiddleware to unpack the response.
    // Instead we use a generic SDK that takes a requester function where we pass the client instead.
    const requester: Requester = (doc, vars) => {
        return client.request(doc, vars as object);
    };
    const sdk = getSdk(requester);

    return {
        /**
         * Typesafe methods to interact with the GraphQL API.
         */
        client: sdk,
        /**
         * base GraphQL client to use for custom requests.
         * Use this if you need to make requests that are not covered by the SDK methods.
         */
        baseClient: client,
    };
};
